import { reactive, computed } from 'vue'
import { useChatSocket } from './useChatSocket'

/**
 * Typing-indicator bus.
 *
 * Server emits `typing.start` / `typing.stop` on DM + global channels when a
 * participant sends the matching frame. We keep a per-thread set of userIds
 * whose "start" we've seen and that haven't yet timed out.
 *
 * The server does no cleanup — if someone's tab crashes after sending
 * `typing.start` we'd leak the indicator forever. So each "start" is only
 * trusted for TYPING_TTL_MS, and the stale entries are swept lazily on read
 * (plus a setInterval fallback).
 *
 * For senders: call `notifyTyping(threadId, kind)` from input handlers. It
 * rate-limits outbound frames (`start` at most once per 2s; `stop` when
 * idle >4s) so we don't spam the WS.
 */

const TYPING_TTL_MS = 5000     // consider remote entries stale after 5s
const START_COOLDOWN_MS = 2000 // rate-limit outbound 'start' frames
const STOP_IDLE_MS = 4000      // after this many idle ms, emit 'stop'

interface TypingEntry { userId: string; updatedAt: number }

// Module-level singleton state. Key = threadId, value = Map<userId, entry>
const byThread = reactive(new Map<string, Map<string, TypingEntry>>())

let wired = false
let sweepTimer: ReturnType<typeof setInterval> | null = null

// Outbound-send bookkeeping per thread
const lastStartSent = new Map<string, number>()
const lastInputAt = new Map<string, number>()
const stopTimers = new Map<string, ReturnType<typeof setTimeout>>()

function prune(threadId: string): void {
    const set = byThread.get(threadId)
    if (!set) return
    const now = Date.now()
    for (const [userId, entry] of set) {
        if (now - entry.updatedAt > TYPING_TTL_MS) set.delete(userId)
    }
    if (set.size === 0) byThread.delete(threadId)
}

function pruneAll(): void {
    for (const threadId of byThread.keys()) prune(threadId)
}

function wire() {
    if (wired) return
    wired = true
    const { on } = useChatSocket()

    on('typing.start', (frame: any) => {
        if (!frame?.threadId || !frame?.userId) return
        let set = byThread.get(frame.threadId)
        if (!set) { set = new Map(); byThread.set(frame.threadId, set) }
        set.set(String(frame.userId), { userId: String(frame.userId), updatedAt: Date.now() })
    })

    on('typing.stop', (frame: any) => {
        if (!frame?.threadId || !frame?.userId) return
        byThread.get(frame.threadId)?.delete(String(frame.userId))
        if (byThread.get(frame.threadId)?.size === 0) byThread.delete(frame.threadId)
    })

    // Lazy GC so stale entries clear even when nobody is rendering a given thread
    if (typeof window !== 'undefined' && !sweepTimer) {
        sweepTimer = setInterval(pruneAll, 2000)
    }
}

export function useTypingIndicator() {
    wire()
    const { user } = useUserSession()
    const meId = computed(() => String((user.value as any)?._id || (user.value as any)?.id || ''))

    /**
     * Reactive list of userIds typing in the given thread, excluding self.
     * Stale entries are pruned on every read.
     */
    function typingUserIds(threadId: string): string[] {
        prune(threadId)
        const set = byThread.get(threadId)
        if (!set) return []
        const ids: string[] = []
        for (const [uid] of set) if (uid !== meId.value) ids.push(uid)
        return ids
    }

    /**
     * Called from message composers on every keystroke. Rate-limits frame
     * emission to at most one `start` per 2s, and emits `stop` after the
     * user has been idle for 4s (or explicitly via `stopTyping`).
     */
    function notifyTyping(threadId: string, kind: 'dm' | 'group' | 'global' | 'bugs' = 'dm'): void {
        if (!threadId) return
        const { send } = useChatSocket()
        const now = Date.now()
        lastInputAt.set(threadId, now)

        const last = lastStartSent.get(threadId) || 0
        if (now - last >= START_COOLDOWN_MS) {
            send({ type: 'typing.start', threadId, kind })
            lastStartSent.set(threadId, now)
        }

        // (Re-)arm the idle-stop timer
        const existing = stopTimers.get(threadId)
        if (existing) clearTimeout(existing)
        stopTimers.set(threadId, setTimeout(() => {
            send({ type: 'typing.stop', threadId, kind })
            lastStartSent.delete(threadId)
            lastInputAt.delete(threadId)
            stopTimers.delete(threadId)
        }, STOP_IDLE_MS))
    }

    /**
     * Explicitly stop typing — called on message send, blur, or component
     * unmount. Idempotent.
     */
    function stopTyping(threadId: string, kind: 'dm' | 'group' | 'global' | 'bugs' = 'dm'): void {
        if (!threadId) return
        const { send } = useChatSocket()
        const existing = stopTimers.get(threadId)
        if (existing) clearTimeout(existing)
        stopTimers.delete(threadId)
        if (lastStartSent.has(threadId)) {
            send({ type: 'typing.stop', threadId, kind })
            lastStartSent.delete(threadId)
            lastInputAt.delete(threadId)
        }
    }

    return { typingUserIds, notifyTyping, stopTyping }
}
